import React, {FC, useCallback, useEffect, useMemo, useState} from "react";
import {css} from "@emotion/css";
import {
    NavigationBar,
    SimpleMenu,
    SimpleMenuItem,
    useMenuState
} from "@octopusdeploy/design-system-components";
import { ChevronDownIcon } from "@octopusdeploy/design-system-icons";
import {themeTokens} from "@octopusdeploy/design-system-tokens";
import repository from "client/repository";
import {MeResource} from "client/resources/meResource";
import {useThemeSetting} from "components/Theme";
import {AutoSubscribeSettingsDialog} from "../../settings/AutoSubscribeSettingsDialog";

const CurrentUserMenu: FC = () => {
    const [me, setMe] = useState<MeResource>({
        Name: "Loading...",
        Email: "",
    });
    useEffect(() => {
        repository.Authentication.me().then(setMe);
    }, []);

    const [openMenu, menuState, buttonAriaAttributes] = useMenuState();
    const [isAutoSubscribeDialogOpen, setIsAutoSubscribeDialogOpen] = useState(false);
    const menuItems = useMenuItems(me, () => setIsAutoSubscribeDialogOpen(true));

    return (
        <div>
            <NavigationBar.Button label={me.Name} icon={<ChevronDownIcon size={24} />} accessibleName="Open User Profile menu"  onClick={openMenu} {...buttonAriaAttributes} />
            <SimpleMenu menuState={menuState} items={menuItems} accessibleName="User Profile" />
            <AutoSubscribeSettingsDialog 
                open={isAutoSubscribeDialogOpen} 
                onClose={() => setIsAutoSubscribeDialogOpen(false)} 
            />
        </div>
    );
};

const infoItemStyle = css`
    color: ${themeTokens.color.text.secondary};
`;

const InfoItem: FC<{ label: string, value: string }> = ({ label, value }) => (
    <span title={label} className={infoItemStyle}>{value}</span>
);

function useMenuItems(me: MeResource, openAutoSubscribeDialog: () => void): SimpleMenuItem[] {
    return [
        {type: "information", content: <InfoItem label="Email" value={me.Email}/>},
        {type: "divider"},
        useDarkThemeMenuItem(),
        autoSubscribeSettingsMenuItem(openAutoSubscribeDialog),
        unsubscribeFromAllBackgroundTasks(),
    ];
}

function useDarkThemeMenuItem(): SimpleMenuItem {
    const { themeName, setThemeName } = useThemeSetting();
    const toggleIsDarkModeEnabled = useCallback((isDarkModeEnabled: boolean) => setThemeName(isDarkModeEnabled ? "dark" : "light"), [setThemeName]);
    const isDarkModeEnabled = themeName === "dark";
    return useMemo(
        () => ({
            type: "toggle",
            label: "Dark theme",
            isEnabled: isDarkModeEnabled,
            setIsEnabled: toggleIsDarkModeEnabled,
        }),
        [isDarkModeEnabled, toggleIsDarkModeEnabled]
    );
}

function autoSubscribeSettingsMenuItem(openDialog: () => void): SimpleMenuItem {
    return {
        type: "button",
        label: "Auto-subscribe settings",
        onClick: openDialog,
    };
}

function unsubscribeFromAllBackgroundTasks(): SimpleMenuItem {
    return {
        type: "button",
        label: "Unsubscribe from all background tasks",
        onClick: async () => { await repository.BackgroundTasks.unsubscribeAll() }
    };
}

export default CurrentUserMenu;
